import { useState, useRef } from "react";
import { ArrowRight, X, FileText } from "lucide-react";
import { Portal } from "@mui/material";

// ── Brand tokens ──────────────────────────────────────────────
const GREEN       = "#1b5e20";   // dark forest green
const GOLD        = "#a58b00";   // warm gold (muted)
const GOLD_BRIGHT = "#d4b800";   // slightly brighter gold for hover
const CREAM       = "#f5f3e8";   // warm cream page background
const CARD_BG     = "#ffffff";   // card surface
const BORDER      = "#d6d2b0";   // muted tan border
const TEXT_MUTED  = "#6b6b4f";   // earthy muted text
const FONT        = "DM Sans, icertxBold, Poppins, sans-serif";

const POPUP_W = 460;

const ServiceCard = ({ icon: iconSrc, title, subtitle, items = [], standards = [] }) => {
  const [open, setOpen]           = useState(false);
  const [showContent, setShowContent] = useState(false);
  const hoverTimeout = useRef(null);
  const wrapperRef   = useRef(null);

  const cols = standards.length === 4 ? 4 : standards.length <= 6 ? 3 : 4;

  const handleOpen = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setOpen(true);
    setTimeout(() => setShowContent(true), 10);
  };

  const handleClose = () => {
    setShowContent(false);
    hoverTimeout.current = setTimeout(() => setOpen(false), 320);
  };

  return (
    <>
      <style>{`
        .sc-card-face:hover {
          border-color: ${GOLD_BRIGHT} !important;
        }
        .sc-explore-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 700;
          color: ${GREEN};
          cursor: pointer;
          padding: 0;
          margin-top: auto;
          flex-shrink: 0;
          background: none;
          border: none;
          font-family: ${FONT};
          transition: color 0.15s, gap 0.15s;
        }
        .sc-explore-btn:hover { color: ${GOLD}; gap: 7px; }
        .sc-popup-btn {
          border-radius: 10px;
          padding: 11px 8px 9px;
          cursor: pointer;
          transition: border-color 0.15s, transform 0.15s;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 7px;
          border: 1px solid ${BORDER};
          background: ${CREAM};
          width: 100%;
          box-sizing: border-box;
          min-width: 0;
          min-height: 108px;
        }
        .sc-popup-btn:hover {
          border-color: ${GOLD_BRIGHT};
          transform: translateY(-2px);
        }
        .sc-popup-close {
          width: 28px; height: 28px;
          border-radius: 8px;
          border: 1px solid ${BORDER};
          background: ${CREAM};
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .sc-popup-close:hover { background: #ede9d0; }
        .sc-grid-scroll { overflow-y: auto; overflow-x: hidden; }
        .sc-grid-scroll::-webkit-scrollbar { width: 5px; }
        .sc-grid-scroll::-webkit-scrollbar-track { background: ${CREAM}; border-radius: 10px; }
        .sc-grid-scroll::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 10px; }
        .sc-card-items::-webkit-scrollbar { width: 3px; }
        .sc-card-items::-webkit-scrollbar-track { background: transparent; }
        .sc-card-items::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 10px; }
      `}</style>

      <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>

        {/* ── CARD FACE ── */}
        <div
          className="sc-card-face"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            overflow: "hidden",
            opacity: open ? 0 : 1,
            borderRadius: 12,
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            cursor: "default",
            transition: "opacity 0.2s, border-color 0.15s",
          }}
        >
          <div style={{
            padding: "10px 12px 8px",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8, flexShrink: 0 }}>
              {/* Icon container */}
              <div style={{
                width: 36, height: 28,
                borderRadius: 8,
                background: GREEN,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, overflow: "hidden",
              }}>
                <img
                  src={iconSrc}
                  alt={title}
                  style={{
                    width: "90%", height: "90%",
                    objectFit: "contain",
                    filter: "brightness(0) invert(1)",
                    transform: "scale(1.5)",
                    transformOrigin: "center",
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: 13,
                  color: GREEN,
                  lineHeight: 1.25,
                  margin: 0,
                }}>{title}</p>
                <p style={{
                  fontFamily: FONT,
                  fontWeight: 400,
                  fontSize: 11,
                  color: TEXT_MUTED,
                  margin: "2px 0 0 0",
                  lineHeight: 1.2,
                }}>{subtitle}</p>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", background: BORDER, marginBottom: 7, flexShrink: 0 }} />

            {/* Bullet items */}
            <ul
              className="sc-card-items"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex", flexDirection: "column", gap: 4,
                flex: 1, minHeight: 0,
                marginBottom: 8, padding: 0, listStyle: "none",
                overflowY: "auto", cursor: "default",
              }}
            >
              {items.map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11.5, color: "#3d3d2a" }}>
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: GOLD,
                    marginTop: 5, flexShrink: 0,
                  }} />
                  <span style={{ lineHeight: 1.4, fontFamily: FONT, fontWeight: 400 }}>{item}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button className="sc-explore-btn" onClick={handleOpen}>
              Explore <ArrowRight size={11} />
            </button>

          </div>
        </div>

        {/* ── POPUP ── */}
        <Portal>
          {open && (
            <>
              <div
                onClick={handleClose}
                style={{
                  position: "fixed", inset: 0, zIndex: 9998,
                  background: "rgba(27,94,32,0.12)",
                  backdropFilter: "blur(2px)",
                  opacity: showContent ? 1 : 0,
                  transition: "opacity 0.22s",
                  cursor: "pointer",
                }}
              />
              <div style={{
                position: "fixed",
                top: "50%", left: "50%",
                transform: showContent ? "translate(-50%,-50%)" : "translate(-50%,calc(-50% + 14px))",
                width: POPUP_W,
                maxHeight: "calc(100vh - 60px)",
                zIndex: 9999,
                borderRadius: 14,
                background: CARD_BG,
                border: `1px solid ${BORDER}`,
                display: "flex", flexDirection: "column",
                boxSizing: "border-box",
                opacity: showContent ? 1 : 0,
                transition: "opacity 0.26s cubic-bezier(0.4,0,0.2,1), transform 0.26s cubic-bezier(0.4,0,0.2,1)",
              }}>

                {/* Popup Header */}
                <div style={{
                  padding: "14px 18px 12px",
                  borderBottom: `1px solid ${BORDER}`,
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                  flexShrink: 0,
                  background: CREAM,
                  borderRadius: "14px 14px 0 0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 48, height: 34,
                      background: GREEN, borderRadius: 8,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, overflow: "hidden",
                    }}>
                      <img src={iconSrc} alt={title}
                        style={{ width: "90%", height: "90%", objectFit: "contain", filter: "brightness(0) invert(1)" }}
                      />
                    </div>
                    <div>
                      <p style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: GREEN, lineHeight: 1.2, margin: 0 }}>
                        {title}
                      </p>
                      <p style={{ fontFamily: FONT, fontSize: 11, fontWeight: 400, color: TEXT_MUTED, margin: "2px 0 0 0" }}>
                        {subtitle}
                      </p>
                    </div>
                  </div>
                  <button className="sc-popup-close" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                    <X size={13} color={TEXT_MUTED} />
                  </button>
                </div>

                {/* Popup Grid */}
                <div
                  className="sc-grid-scroll"
                  style={{
                    padding: "12px 14px 14px",
                    display: "grid",
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    gap: 8,
                    flex: 1, minHeight: 0,
                    alignContent: "start",
                    borderRadius: "0 0 14px 14px",
                    background: CARD_BG,
                  }}
                >
                  {standards.map((std, i) => (
                    <button
                      key={std.label}
                      className="sc-popup-btn"
                      onClick={(e) => { e.stopPropagation(); std.onClick(); }}
                      style={{
                        opacity: showContent ? 1 : 0,
                        transform: showContent ? "translateY(0)" : "translateY(8px)",
                        transition: `opacity 0.2s ease ${i * 22}ms, transform 0.2s ease ${i * 22}ms, border-color 0.15s`,
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: CREAM,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1px solid ${BORDER}`,
                        flexShrink: 0,
                      }}>
                        {std.icon
                          ? <img src={std.icon} alt={std.label} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
                          : <FileText size={13} color={GOLD} />
                        }
                      </div>
                      <div style={{ width: "100%", minWidth: 0 }}>
                        <p style={{
                          fontFamily: FONT, color: GREEN, fontSize: 11, fontWeight: 600,
                          margin: 0, lineHeight: 1.25, textAlign: "center", wordBreak: "break-word",
                        }}>
                          {std.label}
                        </p>
                        <p style={{
                          color: GOLD, fontSize: 9.5, fontWeight: 600,
                          margin: "3px 0 0 0", textAlign: "center", lineHeight: 1,
                          fontFamily: FONT,
                        }}>
                          View →
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </Portal>
      </div>
    </>
  );
};

export default ServiceCard;
